// Ported from authentication/login/page/login.page.tpl.html + login.page.component.ng1.ts.
//
// Markup and class names are verbatim (CLAUDE.md rule 4): `chrome-extension-login-page
// popup-styles`, `woo-form page-content`, `woo-field email|password`, `error`,
// `login-actions`, `or-wrap`/`divider`/`or`, `sso-sign-in-wrap`, `sign-up-wrap`. The ported
// _login.page.scss addresses exactly these, and the Phase-0 baseline is a photograph of the
// production rendering — so a renamed class is a failed diff, not a style preference.
//
// The admin whitelabel picker is deliberately not ported: it renders only for @woo.io
// addresses on the admin build (`ctrl.admin`), reads a whitelabel list from an admin-only
// endpoint, and belongs with the admin-login-helper module rather than with the extension's
// own login. Recorded here rather than dropped silently.
//
// ⚠️ `woo-form page-content` is a DIV, not a `<form>`. Upstream uses `ng-form`, an attribute
// that AngularJS puts on a div — it never renders a form element. A real `<form>` brings the
// browser's own submit behaviour, its default margin, and different intrinsic sizing: the
// ported login screen measured 7px wider than production at 200px until this changed. Enter
// handling is wired per field instead, exactly where upstream's `ng-keydown` sits — on the
// PASSWORD field only (login.page.tpl.html:30). The `<form>` had quietly widened that to
// Enter-from-any-field, which upstream does not do.
import { useCallback, useState, type ReactElement, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAutofocusAttribute } from './autofocusAttribute.ts'
import { useSsoRequiredProbe } from './ssoRequiredProbe.ts'
import { AuthNav } from './AuthNav.tsx'
import { LoadingButton } from './LoadingButton.tsx'
import { PageHost } from './PageHost.tsx'
import { asset } from '../assetBase.ts'

export type OAuthProvider = 'google' | 'microsoft'

export interface LoginPageProps {
  /** Resolves when the credentials are accepted; rejects to show upstream's error block. */
  onSignIn: (email: string, password: string) => Promise<void>
  onSignInOAuth: (provider: OAuthProvider) => void
  /**
   * `sso-required-email-validator`, debounced and failing open — see `ssoRequiredProbe.ts`.
   * Absent means no probe at all, which is the right shape for a caller with no tenant.
   */
  checkSsoRequired?: (email: string) => Promise<boolean>
  /** Upstream gates the whole form on the host permission having been granted. */
  permissionsGranted?: boolean
  onRequestPermissions?: () => void | Promise<unknown>
  /**
   * The `<origin-request>` widget upstream puts inside this block (`sign-in.html:84`): the
   * site checklist AND the button. Supplied by the extension popup, which is the only surface
   * that can read or request Chrome permissions; when it is absent the block falls back to
   * the bare button, which is what the SPA renders (it never shows this branch at all).
   */
  originRequest?: ReactNode
  /**
   * What a rejected `onSignIn` means. Absent, every rejection is "Email and password don't
   * match", which is careero's template exactly — it has one `ctrl.error` and one line.
   *
   * The extension popup supplies this because upstream's popup split it (`sign-in.html`, commit
   * `c64ffd9`): only a real 403 is a credentials problem, and a 400 is "Sign-in failed, please
   * try again." A request nobody answered is neither — the popup navigates away for that one
   * and resolves, so it never reaches here.
   */
  describeSignInFailure?: (error: unknown) => SignInFailureKind
  /**
   * Really focus the email field on mount, rather than render the inert attribute
   * (`autofocusAttribute.ts`). The extension popup's sign-in wins the autofocus race that
   * careero's iframe loses: measured on the store build's popover, and in Avi's own capture,
   * the field opens focused. The popup passes `true`; the SPA must not.
   */
  focusEmail?: boolean
  loading?: boolean
  admin?: boolean
}

export type SignInFailureKind = 'credentials' | 'failed'

interface FieldErrors {
  email: boolean
  password: boolean
  credentials: boolean
  failed: boolean
}

export function LoginPage({
  onSignIn,
  onSignInOAuth,
  checkSsoRequired,
  permissionsGranted = true,
  onRequestPermissions,
  originRequest,
  describeSignInFailure,
  focusEmail = false,
  loading = false,
  admin = false,
}: LoginPageProps): ReactElement {
  const inertAutofocusRef = useAutofocusAttribute()
  const focusingRef = useCallback((node: HTMLElement | null) => {
    node?.setAttribute('autofocus', '')
    node?.focus()
  }, [])
  const autofocusRef = focusEmail ? focusingRef : inertAutofocusRef
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<FieldErrors>({ email: false, password: false, credentials: false, failed: false })
  const sso = useSsoRequiredProbe({ ...(checkSsoRequired === undefined ? {} : { check: checkSsoRequired }) })

  const signIn = async (): Promise<void> => {
    setSubmitted(true)
    const emailInvalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    const passwordInvalid = password.length === 0
    setErrors({ email: emailInvalid, password: passwordInvalid, credentials: false, failed: false })
    if (emailInvalid || passwordInvalid) return
    // ⚠️ AN SSO-ONLY ADDRESS MUST NOT REACH `login`. Upstream's async validator makes the
    // form `$invalid`, so `signIn()` returns before the request; without this the user gets
    // "Email and password don't match" for an account that has no password at all.
    if (sso.ssoRequired || sso.pending) return

    try {
      await onSignIn(email, password)
    } catch (error) {
      // Upstream shows one message for any rejected credential pair and never distinguishes
      // "no such user" from "wrong password" — an account-enumeration boundary that the port
      // keeps rather than improves. `failed` is not that distinction: it is a 400, which is
      // the server failing the request rather than rejecting the pair.
      const kind = describeSignInFailure === undefined ? 'credentials' : describeSignInFailure(error)
      setErrors({ email: false, password: false, credentials: kind === 'credentials', failed: kind === 'failed' })
    }
  }


  // Upstream's error block, condition for condition. `ssoRequired` is one of the email
  // field's `$error` keys there, so it makes the field invalid as well as naming itself.
  const emailInvalid = errors.email || sso.ssoRequired
  const showError = submitted && (emailInvalid || errors.password || errors.credentials || errors.failed)

  return (
    <PageHost name="chrome-extension-login-page" className="popup-styles" hostClassName="non-extended-ui">
      <AuthNav admin={admin} active />

      {loading ? (
        <div className="woo-loader-wrapper">
          <i className="far fa-spinner fa-spin woo-loader" />
        </div>
      ) : permissionsGranted ? (
        <div className="woo-form page-content">
          <input
            className="woo-field email"
            type="email"
            placeholder="Email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              sso.onChange(event.target.value)
            }}
            // `updateOn: 'default blur'` with `debounce.blur: 0` — leaving the field
            // flushes the probe, which is what stops a tab-then-Enter submitting before
            // the address has been checked.
            onBlur={(event) => {
              sso.onBlur(event.target.value)
            }}
            tabIndex={1}
            ref={autofocusRef}
            required
          />

          <input
            className="woo-field password"
            type="password"
            placeholder="Password"
            name="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value)
            }}
            onKeyDown={(event) => {
              // Upstream's onPasswordKeyPress: Enter submits from the password field.
              if (event.key === 'Enter') void signIn()
            }}
            tabIndex={2}
            required
          />

          {showError ? (
            <div className="error">
              {errors.email && !sso.ssoRequired && !errors.password ? (
                <div>Please enter a valid email address</div>
              ) : null}
              {sso.ssoRequired && !errors.password ? <div>Sign in for this email address is via SSO only</div> : null}
              {errors.password && !emailInvalid ? <div>Please enter a password</div> : null}
              {emailInvalid && errors.password ? <div>Please enter an email address &amp; password</div> : null}
              {errors.credentials ? <div>Email and password don&apos;t match</div> : null}
              {errors.failed ? <div>Sign-in failed, please try again.</div> : null}
            </div>
          ) : null}

          {/* `is-disabled="ctrl.form.$pending"`: the button is out while the probe is in
              flight, so Enter cannot submit an address that has not been checked yet. */}
          <LoadingButton text="Sign in" classOverride="form-btn" tabIndex={4} disabled={sso.pending} onClick={signIn} />

          <div className="login-actions">
            <Link className="forgot-password login-action" to="/chrome-extension/auth/forgot-password" tabIndex={5}>
              Forgot your password?
            </Link>
          </div>

          <div className="or-wrap">
            <hr className="divider" />
            <div className="or">or</div>
          </div>

          <div className="sso-sign-in-wrap">
            <button
              type="button"
              className="btn sso-sign-in-btn google-sign-in-btn"
              onClick={() => {
                onSignInOAuth('google')
              }}
            >
              <img className="google-g-logo" src={asset('/static/assets/img/google/google-g-logo.svg')} alt="" />
              <span>Sign in with Google</span>
            </button>
            <button
              type="button"
              className="btn sso-sign-in-btn microsoft-sign-in-btn"
              onClick={() => {
                onSignInOAuth('microsoft')
              }}
            >
              <img
                className="microsoft-entra-id-logo"
                src={asset('/static/assets/img/microsoft/microsoft-entra-id-logo.svg')}
                alt=""
              />
              <span>Sign in with Microsoft</span>
            </button>
          </div>

          <div className="sign-up-wrap">
            Don&apos;t have an account?{' '}
            <Link className="sign-up login-action" to="/chrome-extension/auth/sign-up" tabIndex={6}>
              Sign up
            </Link>
          </div>
        </div>
      ) : (
        <div className="request-permissions page-content">
          <div className="text-content">Add Smart Search to your ATS</div>
          {originRequest ?? (
            <LoadingButton
              text="Add"
              classOverride="form-btn"
              tabIndex={1}
              autoFocus
              onClick={onRequestPermissions ?? ((): void => undefined)}
            />
          )}
        </div>
      )}
    </PageHost>
  )
}
